import { ChildProps } from '@apollo/client/react/hoc';
import { MutationUpdaterFn, gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';

import { CoreHomePageFeed, MixedFeedRow, reverb_feed_FeedEntry_EntryType, RemoveFeedEntry } from '../schemas/rql';
import FeedRow from './feed_row';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import bind from '@reverbdotcom/commons/src/bind';

import { ListingsCollection } from '@reverbdotcom/commons/src/components/listings_collection';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { OverflowingRowAction } from '@reverbdotcom/commons/src/components/overflowing_row_action';
import { Paths } from '../components/shared/url_helpers';
import { mixedFeedFragment } from './mixedFeedFragment';
import { EVERYWHERE_CODE } from '@reverbdotcom/commons/src/constants';

const EXPECTED_NUM = 12;
const MIN_LISTINGS = 6;

type IProps = ChildProps<IUserContext, CoreHomePageFeed.Query, CoreHomePageFeed.Variables>;
/**
 * RQLHomePageFeed is only rendered for logged in users. The hook
 * for that decision is up a layer in the Homepage itself
 */
export class RQLHomePageFeed extends React.Component<IProps, {}> {
  static entryIds(props: IProps) {
    const entries = props.data?.me?.feed?.entries ?? [];
    return entries.map(entry => entry.id);
  }

  get entries(): MixedFeedRow.Fragment[] {
    return (this.props.data?.me?.feed?.entries ?? []).filter(entry => !entry?.deleted);
  }


  get listings(): MixedFeedRow.ListingInlineFragment[] {
    return this
      .entries
      .filter(entry => entry.type === reverb_feed_FeedEntry_EntryType.LISTING)
      .map(entry => entry.entry as MixedFeedRow.ListingInlineFragment);
  }

  @bind
  removeFeedItemFromCache(entryId: number): MutationUpdaterFn<RemoveFeedEntry.Mutation> {
    const { data } = this.props;

    return (cache) => {
      if (!data?.me?.feed?.entries) {
        return;
      }

      const deletedIndex = data.me.feed.entries.findIndex(e => e.id === entryId);
      if (deletedIndex > 0) {
        data.me.feed.entries[deletedIndex].deleted = true;
      }

      cache.writeQuery({
        data,
        variables: data.variables,
        query: gqlQuery,
      });

      data.refetch();
    };
  }

  render() {
    const { entries } = this;

    const minListings = MIN_LISTINGS;

    if (!this.props.data.loading && entries.length < minListings) {
      return null;
    }

    return (
      <FeedRow
        title={I18n.t('discovery.feed.shortTitle')}
        cta={
          <OverflowingRowAction
            href={Paths.myFavoritesFeed.expand({})}
            text={I18n.t('discovery.feed.seeMoreCTA')}
          />
        }
        expectedCount={EXPECTED_NUM}
        loading={this.props.data.loading}
        singleRow
        entries={entries}
        removeFeedItemFromCache={this.removeFeedItemFromCache}
        hideRemoveButton={true}
        largeTiles={false}
      />
    );
  }
}

const gqlQuery = gql`
  query Core_HomePage_Feed(
    $limit: Int
  ) {
    me {
      uuid
      feed(input: { channel: REVERB, offset: 0, limit: $limit }) {
        total
        offset
        limit
        entries {
          ...MixedFeedRow
          entry {
            ... on Listing {
              _id
            }
          }
        }
      }
    }
  }
  ${mixedFeedFragment}
  ${ListingsCollection.fragments.listing}
`;

const connect = withGraphql<IProps, CoreHomePageFeed.Query, CoreHomePageFeed.Variables>(
  gqlQuery,
  {
    options: (_props) => {
      return {
        // Ignore sticky seller location on Homepage.
        context: {
          headers: {
            'X-Item-Region': EVERYWHERE_CODE,
          },
        },
        ssr: true,
        variables: {
          limit: 6,
        },
      };
    },
  },
);

export default withUserContext(connect(RQLHomePageFeed));
